import { useDeleteNetLockMutation } from '@api/front-office/netlock.api.rtk';
import { useGetProfileDetailsQuery } from '@api/front-office/profile.api.rtk';
import { useLazyGetTableDetailsQuery } from '@api/pos/pos-bo-table-management.api.rtk';
import {
  useCreateTransactionMutation,
  useUpdateTransactionMutation,
} from '@api/pos/pos-transaction-api.rtk';
import DrawerWrapper from '@components/drawer/drawer-wrapper';
import AutoCompleteController from '@components/form/controller/autocomplete.controller';
import CheckboxController from '@components/form/controller/checkbox.controller';
import GroupTitle from '@components/form/group-title';
import DataTable from '@components/table/datatable';
import { yupResolver } from '@hookform/resolvers/yup';
import useDisclosure, { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import { optionsVATTypeOptions } from '@modules/front-office/components/profile/fo-profile-individual-drawer';
import { selectedTableSchema } from '@modules/point-of-sale/interface/pos-table-schema/pos-table-details-schema';
import {
  CreateTransactionPayload,
  defaultValues,
  IPOSProfileInfo,
  IPOSTableDetailsFormSchema,
} from '@modules/point-of-sale/interface/table-details.interface';
import {
  selectAccountMasterlist,
  selectPOSLocationMasterlist,
  selectPOSSelectedTable,
  selectPOSServiceOptions,
  selectPOSstationID,
  selectPOSsubModuleCode,
  selectPOSTableRefID,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { NumbersOutlined } from '@mui/icons-material';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { GridColDef, GridRowId } from '@mui/x-data-grid';
import { useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { isSaveDisabled } from '@utils/form';
import { TinPatternFormat } from '@utils/format-component';
import { hashSignature } from '@utils/security';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';
import { memo, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';
import InputFieldsController from '../../../../../../components/form/controller/input-fields.controller';
import { usePOSWindowDrawerCtx } from '../pos-order-taking-window';
import POSTableTaxPayerDetails from './pos-table-tax-payer-details';
import useAppContext from '@hooks/useAppContext';
import CustomNumberFormat from '@components/form/custom-number-format';

const accountColumns: GridColDef[] = [
  {
    field: 'first_name',
    headerName: 'Name',
    width: 150,
    valueParser(value, params) {
      return value + ` ${params?.row?.last_name ?? ''}`;
    },
    flex: 1,
  },
  {
    field: 'nationality',
    headerName: 'Nationality',
    width: 150,
  },
];

const POSAccountsModal = ({
  modalDisclosure,
  getSelectedRow,
}: {
  modalDisclosure: UseDisclosureInterface;
  getSelectedRow: (id: GridRowId) => void;
}) => {
  const accounts = useAppSelector(selectAccountMasterlist);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);

  const handleModalClose = () => {
    modalDisclosure.onClose();
    setSelectedRows([]);
  };

  return (
    <ModalWrapper
      {...{
        modalDisclosure,
        withHeader: {
          title: `Accounts`,
          onClose: handleModalClose,
        },
      }}
    >
      <Stack
        sx={{
          flex: 1,
          minHeight: 400,
          minWidth: { xs: '100%', md: '600px' },
          maxHeight: 600,
        }}
      >
        <DataTable
          getRowId={(row) => row?.account_id}
          columns={accountColumns}
          checkboxSelection={false}
          rows={accounts ?? []}
          disableRowSelectionOnClick
          tableStyle={{
            padding: 0,
            height: 'unset',
          }}
          wrapperStyle={{
            height: 'unset',
            display: 'flex',
            flex: 1,
            minHeight: 0,
          }}
          rowSelectionModel={selectedRows}
          onRowClick={(data) =>
            setSelectedRows((prev) => {
              const updated = prev.includes(data.id)
                ? prev.filter((i) => i !== data.id)
                : [data.id];
              return updated;
            })
          }
        />
        {/* BUTTON */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={2}
          sx={{
            justifyContent: 'flex-end',
            borderTop: '1px solid #EEE',
            pt: 2,
          }}
        >
          <Button variant='outlined' onClick={handleModalClose}>
            Close
          </Button>
          <LoadingButton
            variant='contained'
            disabled={selectedRows.length <= 0}
            onClick={() => {
              getSelectedRow(selectedRows[0]);
              handleModalClose();
            }}
          >
            Select
          </LoadingButton>
        </Stack>
      </Stack>
    </ModalWrapper>
  );
};

const POSTableDetailsDrawer = () => {
  //#region STATE
  const selectedPropertyID = useAppSelector(selectPropertyID);
  const postStationID = useAppSelector(selectPOSstationID);
  const serviceOptions = useAppSelector(selectPOSServiceOptions);
  const nationalityList = useAppSelector((state) => state.nationalityList);
  const selectedTransactionId = useAppSelector(selectPOSTableTransactionID);
  const selectedTable = useAppSelector(selectPOSSelectedTable);
  const tableRefID = useAppSelector(selectPOSTableRefID);
  const locations = useAppSelector(selectPOSLocationMasterlist);
  const [selectedRow, setSelectedRow] = useState<GridRowId>();
  const [isGuestAccountCleared, setIsGuestAccountCleared] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] =
    useState<IPOSTableDetailsFormSchema>();
  const accountsDisclosure = useDisclosure();
  const nameUpdateDisclosure = useDisclosure();
  const subModuleCode = useAppSelector(selectPOSsubModuleCode);

  const {
    genpar: { getGenparValue },
    openSecCodeDialog,
  } = useAppContext();
  const nonVATable = getGenparValue('comp_vat_type') === 'NV';
  const scPercentRate = getGenparValue('comp_scrate_percentage', subModuleCode);

  const {
    tableDetailsDrawerDisclosure: { isOpen, onClose },
    autoSelectTableState,
    tableDrawerDisclosure,
  } = usePOSWindowDrawerCtx();
  const setAutoSelectTable = autoSelectTableState[1];

  const [guestType, setGuestType] = useState<'In-House' | 'Walk-In'>(
    'In-House',
  );
  const { isInHouse, isWalkIn } = useMemo(() => {
    return {
      isInHouse: guestType === 'In-House',
      isWalkIn: guestType === 'Walk-In',
    };
  }, [guestType]);

  const { toast } = useToast();
  //#endregion

  //#region API
  const [getTableDetails, { isLoading, isFetching }] =
    useLazyGetTableDetailsQuery();

  const [createTransaction, createTransactionResponse] =
    useCreateTransactionMutation();
  const [updateTransaction, updateTransactionResponse] =
    useUpdateTransactionMutation();

  const {
    profileData,
    isFetching: isFetchingDetails,
    isLoading: isLoadingDetails,
  } = useGetProfileDetailsQuery(selectedRow, {
    skip: !isOpen || !selectedRow,
    selectFromResult: ({ data, ...item }) => ({
      ...item,
      profileData: data?.data?.profiles?.[0]?.profile,
    }),
  });
  const [deleteNetLock] = useDeleteNetLockMutation();

  //#endregion

  //#region FORMS
  const methods = useForm<IPOSTableDetailsFormSchema>({
    mode: 'onSubmit',
    resolver: yupResolver(selectedTableSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    formState: { errors, dirtyFields },
    reset,
    setValue,
    getValues,
    control,
  } = methods;

  const hasServiceCharge = useWatch({
    control,
    name: 'serviceCharge',
  });

  const selectedAccountRefID = useWatch({
    control,
    name: 'guestAccountRefID',
  });

  // Handle service charge checkbox changes
  useEffect(() => {
    if (hasServiceCharge) {
      // If checked set to genparSCRate value if not already set
      const currentRate = getValues('serviceChargeRate');

      if (!currentRate || currentRate === '0') {
        const defaultRate = Number(scPercentRate) * 100 || 0;
        setValue('serviceChargeRate', defaultRate.toString());
      }
    } else {
      setValue('serviceChargeRate', '0');
    }
  }, [
    hasServiceCharge,
    getValues,
    scPercentRate,
    selectedTransactionId,
    isOpen,
    setValue,
  ]);

  useLayoutEffect(() => {
    (async () => {
      try {
        if (!selectedTransactionId) {
          reset(defaultValues);
          setIsGuestAccountCleared(false);
          return;
        }

        if (!isOpen) return;

        const raw = await getTableDetails(selectedTransactionId).unwrap();

        const data = raw?.results?.[0];
        setIsGuestAccountCleared(false);

        const merged = {
          ...defaultValues,
          ...data,
          vatType:
            optionsVATTypeOptions.find(
              (item) =>
                item.value === (data?.vatType ?? defaultValues?.vatType),
            )?.label ?? '',
          serviceCharge: Number(data.serviceCharge) > 0 ? true : false,
          serviceChargeRate: (Number(data.serviceCharge) * 100).toString(),
          guestAccountRefID: data.accountID,
        };

        reset(merged);
      } catch (error) {
        toast({ severity: 'warning', message: error });
      }
    })();
  }, [selectedTransactionId, reset, isOpen, getTableDetails, toast]);

  useEffect(() => {
    if (!profileData || !selectedRow || !isOpen) return;
    const {
      caption,
      tinNo,
      taxBusStyle,
      address,
      province,
      countryName,
      vatType,
    } = profileData;

    const keys = {
      busName: caption,
      taxTIN: tinNo,
      busType: taxBusStyle,
      homeAddress: address,
      province,
      countryCode: countryName,
      vatType,
    };

    Object.keys(keys).forEach((item) =>
      setValue(
        item as keyof IPOSTableDetailsFormSchema,
        (keys as IPOSTableDetailsFormSchema)?.[item] ?? '',
      ),
    );
  }, [profileData, selectedRow, isOpen, setValue]);
  // #endregion

  // #region FUNCTION
  const onSubmit: SubmitHandler<IPOSTableDetailsFormSchema> = async (data) => {
    const {
      vatType,
      totalPax,
      serviceType,
      guestName,
      nationality,
      remarks,

      // TAX PAYER INFO (OPTIONAL)
      busName,
      taxTIN,
      busType,
      homeAddress,
      province,
      countryCode,
      guestAccountRefID,
      serviceChargeRate,
    } = data;

    const taxpayerFieldNames: Array<keyof IPOSTableDetailsFormSchema> = [
      'busName',
      'taxTIN',
      'busType',
      'homeAddress',
      'province',
      'countryCode',
    ];

    const updatedVatType =
      optionsVATTypeOptions.find((item) => item.label === vatType)?.value ?? '';

    const selectedServiceOption = serviceOptions.find(
      (opt) => opt.description === serviceType,
    );

    const serviceOptionID = selectedServiceOption
      ? selectedServiceOption._id
      : 1;

    const nationalityID =
      nationalityList.find((item) => item?.code === nationality)?._id || 1;

    const countryCodeState =
      nationalityList.find((item) => item?.caption === countryCode)?.code ??
      null;

    const hasTaxpayerFieldChanges = taxpayerFieldNames.some(
      (fieldName) => dirtyFields[fieldName],
    );

    const hasAccountChange =
      Boolean(selectedRow) || isGuestAccountCleared || hasTaxpayerFieldChanges;

    const profileInfo = [
      selectedRow ?? guestAccountRefID,
      busName,
      taxTIN?.split('-')?.join('') ?? '',
      busType,
      homeAddress,
      province ?? 'null',
      countryCodeState ?? 'null',
      (profileData?.listContacts?.find((item) => item.isPrimary)?.contact ||
        profileData?.listContacts?.find((item) => item.contact)?.contact) ??
        '0',
      (profileData?.listEmails?.find((item) => item.isPrimary)?.email ||
        profileData?.listEmails?.find((item) => item.email)?.email) ??
        '',
      profileData?.profileType ?? 'Company',
      isGuestAccountCleared,
    ];

    const signature = hashSignature(
      [
        selectedPropertyID, // propertyID
        postStationID, // stationID
        tableRefID, // tableID
        Number(serviceChargeRate) / 100, // serviceChargeRate in decimal
        'null', // modifyLocalTaxPercentage
        updatedVatType ?? 'null', // modifyVatType
        totalPax, // totalPax
        serviceOptionID, // serviceOptionID
        guestName, // alternateGuestName
        nationalityID, // nationalityID
        remarks ?? 'null', // remarksNote
        'null', // representationExpenseID
        ...(hasAccountChange ? profileInfo : []),
      ].join(''),
    );

    // Build common transaction object
    const baseTransaction = {
      stationID: postStationID,
      tableID: tableRefID,
      modifyServiceChargePercentage: Number(serviceChargeRate) / 100,
      modifyLocalTaxPercentage: null,
      modifyVatType: updatedVatType,
      totalPax: Number(totalPax),
      serviceOptionID,
      alternateGuestName: guestName,
      remarksNote: remarks,
      representationExpenseID: null,
      nationalityID,
      signature,
      ...(hasAccountChange && {
        profileInfo: {
          guestAccountRefID: profileInfo[0] as string | null,
          caption: profileInfo[1],
          taxTIN: profileInfo[2],
          taxBusStyle: profileInfo[3],
          address: profileInfo[4],
          province: profileInfo[5] === 'null' ? null : profileInfo[5],
          country: profileInfo[6] === 'null' ? null : profileInfo[6],
          contact: profileInfo[7],
          email: profileInfo[8],
          accountType: profileInfo[9],
          clearGuestAccount: isGuestAccountCleared,
        } as IPOSProfileInfo,
      }),
    };

    // Full payload (works for both create + update)
    const transactionPayload = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            posTransactions: [
              {
                posTransaction: {
                  ...baseTransaction,
                  ...(selectedTransactionId
                    ? {
                        // update-specific overrides
                      }
                    : {
                        // create-specific fields
                        // defaultServiceChargePercentage: null,
                        // defaultLocalTaxPercentage: null,
                        // defaultVatType: null, // tableDetails?.[0]?.vatType
                      }),
                },
              },
            ],
          },
        },
      ],
    };

    // Ask for sec code when service charge is changed to zero.
    const serviceChargeChangedToZero =
      serviceChargeRate === '0' &&
      ((dirtyFields.serviceCharge && !hasServiceCharge) ||
        dirtyFields.serviceChargeRate);

    if (serviceChargeChangedToZero) {
      await openSecCodeDialog(async (res) => {
        if (!res?.data.isValid) return;
      }, 'remove_sc');
    }

    try {
      if (selectedTransactionId) {
        await updateTransaction({
          payload: transactionPayload,
          refId: selectedTransactionId,
          hasAccountID: hasAccountChange,
        }).unwrap();

        toast({
          severity: 'success',
          message: 'Success Updating a Transaction',
        });

        setAutoSelectTable('');
        handleDrawerClose();
        tableDrawerDisclosure.onClose();
      } else {
        const result = await createTransaction({
          payload: transactionPayload as CreateTransactionPayload,
          hasAccountID: hasAccountChange,
        }).unwrap();

        setAutoSelectTable(result.transactionID);

        toast({
          severity: 'success',
          message: 'Table Transaction added successfully',
        });
      }
    } catch (err) {
      toast({
        severity: 'error',
        message: err,
      });
    } finally {
      handleDrawerClose();
    }
  };

  const handleClearSelectedAccount = () => {
    setSelectedRow(undefined);
    setIsGuestAccountCleared(true);

    setValue('guestAccountRefID', '', {
      shouldDirty: true,
    });

    setValue('busName', '', {
      shouldDirty: true,
    });
  };

  const handleSelectAccount = (id: GridRowId) => {
    setSelectedRow(id);
    setIsGuestAccountCleared(false);

    methods.resetField('busName', {
      defaultValue: getValues('busName'),
    });
  };

  const handleFormSubmit: SubmitHandler<IPOSTableDetailsFormSchema> = async (
    data,
  ) => {
    const hasSelectedAccount = Boolean(selectedRow ?? data.guestAccountRefID);
    const hasBusinessNameChanged = dirtyFields.busName || false;

    if (hasSelectedAccount && hasBusinessNameChanged) {
      setPendingSubmitData(data);
      nameUpdateDisclosure.onOpen();
      return;
    }

    await onSubmit(data);
  };

  const handleDrawerClose = async () => {
    onClose();
    setSelectedRow(undefined);
    setIsGuestAccountCleared(false);
    setPendingSubmitData(undefined);
    nameUpdateDisclosure.onClose();
    reset(defaultValues);
    selectedRow &&
      deleteNetLock(`${selectedRow}/Profile`)
        .unwrap()
        .catch(() =>
          toast({
            severity: 'error',
            message: 'Error deleting of net lock on database.',
          }),
        );
  };
  // #endregion

  return (
    <FormProvider {...methods}>
      <DrawerWrapper
        {...{
          open: isOpen,
          onClose: handleDrawerClose,
          withHeader: {
            title: 'Table: ' + selectedTable?.tableAcr,
            learnMore: () => {},
            sx: { minHeight: 0, flex: 1, overflow: 'hidden' },
          },
          isLoading:
            isLoading || isFetching || isFetchingDetails || isLoadingDetails,
        }}
      >
        <Stack
          component='form'
          sx={{
            flex: 1,
            width: { xs: '100%', md: '800px' },
            minHeight: 0,
          }}
          onSubmit={handleSubmit(handleFormSubmit)}
        >
          {/* MAIN */}
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
          >
            {/* COLUMN 1 */}

            <Stack sx={{ p: 2, flex: 1 }}>
              <GroupTitle title='Table Information' />
              <Stack sx={{ gap: 2 }}>
                <InputFieldsController
                  control={control}
                  errors={errors}
                  label='Trans No.'
                  name='transNo'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <NumbersOutlined />
                      </InputAdornment>
                    ),
                  }}
                  disabled
                />

                <InputFieldsController
                  control={control}
                  errors={errors}
                  label='Name'
                  name='guestName'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                />

                <AutoCompleteController
                  control={control}
                  errors={errors}
                  label='Nationality *'
                  name='nationality'
                  options={nationalityList?.map((item) => item?.code) || []}
                  style={{ margin: 0 }}
                  textFieldProps={{
                    InputLabelProps: { shrink: true },
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Tab') {
                      e.stopPropagation();
                      return;
                    }
                  }}
                  disableClearable
                />

                <Stack direction='row' gap={2}>
                  <InputFieldsController
                    className='hide-additional-btn'
                    control={control}
                    errors={errors}
                    label='Table Pax *'
                    name='totalPax'
                    wrapperStyle={{
                      marginBottom: 0,
                    }}
                    type='number'
                    autoComplete='off'
                    InputProps={{
                      inputComponent: CustomNumberFormat,
                      inputProps: {
                        allowNegative: false,
                        isNegative: false,
                        maxAmount: 999,
                        minAmount: 1,
                        textAlign: 'left',
                        decimalScale: 0,
                        onKeyDown: (e) => {
                          if (['-', 'e', 'E', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        },
                      },
                    }}
                  />
                  <AutoCompleteController
                    control={control}
                    errors={errors}
                    label='Service *'
                    name='serviceType'
                    options={
                      serviceOptions.map((option) => option.description) || []
                    }
                    style={{ margin: 0 }}
                    disableClearable
                  />
                </Stack>
              </Stack>

              <GroupTitle
                title='Guest Type'
                wrapperStyle={{ marginTop: 2, marginBottom: 2 }}
              />

              <Stack sx={{ gap: 2 }}>
                <Stack direction='row' gap={1}>
                  <Chip
                    label='In-House'
                    variant={isInHouse ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isInHouse ? 'primary.main' : 'initial',
                      color: isInHouse ? '#FFF' : 'initial',
                      '& .MuiTouchRipple-root': {
                        display: 'none',
                      },
                    }}
                    onClick={() => {
                      setValue('guestType', 'In-House');
                      setGuestType('In-House');
                    }}
                  />
                  <Chip
                    label='Walk-In'
                    variant={isWalkIn ? 'filled' : 'outlined'}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: isWalkIn ? 'primary.main' : 'initial',
                      color: isWalkIn ? '#FFF' : 'initial',
                      '& .MuiTouchRipple-root': {
                        display: 'none',
                      },
                    }}
                    onClick={() => {
                      setValue('guestType', 'Walk-In');
                      setGuestType('Walk-In');
                    }}
                  />
                </Stack>

                <InputFieldsController
                  control={control}
                  errors={errors}
                  label='Remarks'
                  name='remarks'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                  multiline
                  rows={5}
                  inputStyle={{
                    '.MuiInputBase-root': { height: '' },
                  }}
                  inputProps={{ maxLength: 999 }}
                  showTextLength
                  placeholder=' '
                />
              </Stack>
            </Stack>

            <Divider
              orientation='vertical'
              sx={{ display: { xs: 'none', md: 'block' } }}
            />
            <Divider
              orientation='horizontal'
              sx={{ display: { xs: 'block', md: 'none' } }}
            />

            {/* COLUMN 2 */}
            <Stack sx={{ p: 2, flex: 1 }}>
              <GroupTitle title='Taxpayer Information (Optional)' />
              <Stack sx={{ gap: 2 }}>
                <InputFieldsController
                  control={control}
                  errors={errors}
                  label="Business / Person's Name"
                  name='busName'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end' sx={{ gap: '4px' }}>
                        {(selectedRow !== undefined ||
                          !!selectedAccountRefID) && (
                          <IconButton
                            type='button'
                            size='small'
                            disableRipple
                            aria-label='Clear selected account'
                            onClick={handleClearSelectedAccount}
                          >
                            <ClearIcon sx={{ fontSize: '18px' }} />
                          </IconButton>
                        )}
                        <IconButton
                          edge='end'
                          onClick={accountsDisclosure.onOpen}
                          style={{
                            padding: 2,
                            marginRight: '-8px',
                            backgroundColor: '#2B3493',
                            borderRadius: '4px',
                            color: '#ffffff',
                          }}
                        >
                          <SearchIcon sx={{ fontSize: '18px' }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <InputFieldsController
                  control={control}
                  errors={errors}
                  label='TIN'
                  name='taxTIN'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                  InputProps={{
                    inputComponent: TinPatternFormat,
                  }}
                />

                {/* <InputFieldsController
                  control={control}
                  errors={errors}
                  label='Business Type'
                  name='busType'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                /> */}

                <InputFieldsController
                  control={control}
                  errors={errors}
                  label='Home Address'
                  name='homeAddress'
                  wrapperStyle={{
                    marginBottom: 0,
                  }}
                />

                <Stack direction='row' gap={2}>
                  <AutoCompleteController
                    control={control}
                    errors={errors}
                    label='City / Province'
                    name='province'
                    options={
                      locations.map((location) => location.caption) || []
                    }
                    style={{ margin: 0 }}
                  />
                  <AutoCompleteController
                    control={control}
                    errors={errors}
                    label='Country'
                    name='countryCode'
                    options={
                      nationalityList.map(
                        (nationality) => nationality.caption,
                      ) || []
                    }
                    style={{ margin: 0 }}
                    textFieldProps={{
                      InputLabelProps: { shrink: true },
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        e.stopPropagation();
                        return;
                      }
                    }}
                  />
                </Stack>

                <Stack>
                  {false && (
                    <AutoCompleteController
                      control={control}
                      errors={errors}
                      label='Expense Type'
                      name='expenseType'
                      options={[
                        'Representation Expense',
                        'Entertainment Expense',
                      ]}
                      style={{ mb: 2 }}
                      disableClearable
                    />
                  )}
                  {!nonVATable && (
                    <AutoCompleteController
                      control={control}
                      errors={errors}
                      label='Tax Registration'
                      name='vatType'
                      options={optionsVATTypeOptions
                        ?.filter((item) => {
                          // Remove 'Non VAT'
                          if (!nonVATable) {
                            return item.label !== 'Non VAT';
                          }
                        })
                        ?.map((item) => item.label)}
                      style={{ margin: 0 }}
                      disableClearable
                    />
                  )}
                </Stack>

                <Stack>
                  <CheckboxController
                    control={control}
                    name='serviceCharge'
                    label='Service Charge'
                    etc={{ defaultChecked: true }}
                  />

                  {hasServiceCharge && (
                    <InputFieldsController
                      className='hide-additional-btn'
                      control={control}
                      errors={errors}
                      label='Service Charge Value in % *'
                      name='serviceChargeRate'
                      wrapperStyle={{
                        marginBottom: 0,
                        pt: 1,
                      }}
                      type='number'
                      autoComplete='off'
                      inputProps={{
                        min: 0,
                        max: 10,
                        onKeyDown: (e) => {
                          // Block negative sign, 'e', 'E', and '.'
                          if (['-', 'e', 'E', '.'].includes(e.key)) {
                            e.preventDefault();
                          }
                        },
                        onChange: (e) => {
                          const input = e.target as HTMLInputElement;
                          const value = parseFloat(e.target.value) || 0;
                          const clampedValue = Math.min(10, Math.max(0, value));

                          if (value <= 0) {
                            input.value = '0';
                            const event = new Event('change', {
                              bubbles: true,
                            });
                            input.dispatchEvent(event);
                          }

                          if (value !== clampedValue) {
                            e.target.value = clampedValue.toString();
                            // Optional: Trigger change event if needed
                            e.target.dispatchEvent(
                              new Event('change', { bubbles: true }),
                            );
                          }
                        },
                      }}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Stack>

          {/* BUTTONS */}
          <Box>
            <Divider />
            <Stack direction='row' gap={1} p={2} justifyContent='flex-end'>
              <Button variant='outlined' onClick={handleDrawerClose}>
                Close
              </Button>
              <LoadingButton
                variant='contained'
                color='primary'
                type='submit'
                disabled={isSaveDisabled(errors)}
                loading={
                  createTransactionResponse.isLoading ||
                  updateTransactionResponse.isLoading ||
                  isLoading ||
                  isFetching
                }
                sx={{
                  width: '100px',
                }}
              >
                {selectedTransactionId ? 'Update' : 'Save'}
              </LoadingButton>
            </Stack>
          </Box>
        </Stack>
      </DrawerWrapper>

      <POSTableTaxPayerDetails />
      <ConfirmationDialog
        open={nameUpdateDisclosure.isOpen}
        title='Confirm Business / Person Name Update'
        desc={
          <Stack>
            <Typography>
              Are you sure you want to update the Business/Person Name?
            </Typography>
            <Typography>
              The existing name will be replaced with the new entered value.
            </Typography>
          </Stack>
        }
        cancelButtonTitle='Cancel'
        confirmButtonTitle='Confirm'
        onClose={() => {
          setPendingSubmitData(undefined);
          nameUpdateDisclosure.onClose();
        }}
        onConfirm={async () => {
          if (!pendingSubmitData) return;

          const data = pendingSubmitData;
          setPendingSubmitData(undefined);
          nameUpdateDisclosure.onClose();
          await onSubmit(data);
        }}
      />
      <POSAccountsModal
        modalDisclosure={accountsDisclosure}
        getSelectedRow={handleSelectAccount}
      />
    </FormProvider>
  );
};

export default memo(POSTableDetailsDrawer);
